import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Video, Phone, Mic, MicOff, VideoOff } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';

const Telemedicine = ({ user, setUser }) => {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);

  useEffect(() => {
    loadAppointment();
  }, [appointmentId]);

  const loadAppointment = async () => {
    try {
      const response = await axios.get(`${API}/telemedicine/room/${appointmentId}`);
      setAppointment(response.data.appointment);
    } catch (error) {
      console.error('Error loading appointment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEndCall = () => {
    navigate('/appointments');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-white mx-auto mb-4"></div>
          <p>Loading consultation room...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <Navigation user={user} setUser={setUser} />
      
      <div className="container mx-auto px-4 py-8 sm:py-8 pt-24 " data-testid="telemedicine-page">
        <Card className="bg-gray-800 border-gray-700 p-8 mb-6">
          <h1 className="text-3xl font-bold text-white mb-2">Video Consultation</h1>
          <p className="text-gray-400">
            {appointment?.doctor_name} • {appointment?.appointment_date} at {appointment?.appointment_time}
          </p>
        </Card>

        <div className="grid lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3">
            <Card className="bg-gray-800 border-gray-700 aspect-video flex items-center justify-center">
              <div className="text-center">
                <Video className="w-24 h-24 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg mb-2">Video consultation would appear here</p>
                <p className="text-gray-500 text-sm">
                  In production, this would integrate with WebRTC for real-time video
                </p>
              </div>
            </Card>

            <div className="flex justify-center gap-4 mt-6">
              <Button
                onClick={() => setAudioEnabled(!audioEnabled)}
                className={`w-14 h-14 rounded-full ${audioEnabled ? 'bg-gray-700 hover:bg-gray-600' : 'bg-red-600 hover:bg-red-700'}`}
              >
                {audioEnabled ? <Mic className="w-6 h-6" /> : <MicOff className="w-6 h-6" />}
              </Button>
              <Button
                onClick={() => setVideoEnabled(!videoEnabled)}
                className={`w-14 h-14 rounded-full ${videoEnabled ? 'bg-gray-700 hover:bg-gray-600' : 'bg-red-600 hover:bg-red-700'}`}
              >
                {videoEnabled ? <Video className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}
              </Button>
              <Button
                onClick={handleEndCall}
                className="w-14 h-14 rounded-full bg-red-600 hover:bg-red-700"
              >
                <Phone className="w-6 h-6 rotate-135" />
              </Button>
            </div>
          </div>

          <div className="space-y-4">
            <Card className="bg-gray-800 border-gray-700 p-4">
              <h3 className="text-white font-semibold mb-3">Consultation Details</h3>
              <div className="space-y-2 text-sm text-gray-400">
                <p><strong className="text-gray-300">Doctor:</strong> {appointment?.doctor_name}</p>
                <p><strong className="text-gray-300">Patient:</strong> {appointment?.patient_name}</p>
                <p><strong className="text-gray-300">Date:</strong> {appointment?.appointment_date}</p>
                <p><strong className="text-gray-300">Time:</strong> {appointment?.appointment_time}</p>
              </div>
            </Card>

            <Card className="bg-gray-800 border-gray-700 p-4">
              <h3 className="text-white font-semibold mb-3">Notes</h3>
              <textarea
                className="w-full bg-gray-700 text-white p-3 rounded-lg border border-gray-600 min-h-32"
                placeholder="Add consultation notes..."
              ></textarea>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Telemedicine;
